import { ExternalLinkIcon, LockIcon } from "lucide-react";
import React, { useState, useRef } from "react";
import { TextInputBox } from "src/components";
import { DateInputBox } from "src/components/ui/inputs/dateInputBox";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { GXO_WAREHOUSE_ADDRESS } from "src/constants/addresses";
import {
  PH_TARIFF_CODE,
  PH_GOODS_DESCRIPTION,
  PH_HEALF_EORI_UK,
  PH_HEALF_VAT_UK,
  PH_HEALF_CONTACT_UK
} from "src/constants/parcelHero";
import { ISOString } from "src/types";
import { VendorShipmentDetails } from "src/types/database/vendor";
import { ParcelHeroShippingConfiguration } from "src/types/parcelHeroTimeline";

interface ModalProps {
  purchaseOrderId: string;
  shippingConfiguration?: ParcelHeroShippingConfiguration;
  vendorName?: string;
  vendorEmails?: string[];
  vendorOrigin?: string;
  vendorShipmentDetails?: VendorShipmentDetails;
  hmrcTotalCost?: number;
  totalCost?: number;
}

interface ConfirmationProps extends ModalProps {
  onCancel: () => void;
  onConfirm: (shippingConfiguration: ParcelHeroShippingConfiguration) => void;
}

const getNextBusinessDay = (): string => {
  const now = new Date();
  const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  while (date.getUTCDay() === 0 || date.getUTCDay() === 6) {
    date.setUTCDate(date.getUTCDate() + 1);
  }
  return date.toISOString().split("T")[0];
};

const StartParcelHeroConfirmation: React.FC<ConfirmationProps> = ({
  purchaseOrderId,
  shippingConfiguration,
  vendorName,
  vendorEmails,
  vendorOrigin,
  vendorShipmentDetails,
  hmrcTotalCost,
  totalCost,
  onConfirm,
  onCancel
}) => {
  const effectiveCost = hmrcTotalCost ?? totalCost;
  const costLabel = hmrcTotalCost !== undefined ? "HMRC Total Cost" : "Total Cost";
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [warehouseAddress, setWarehouseAddress] = useState({
    company: shippingConfiguration?.warehouseAddress?.company || GXO_WAREHOUSE_ADDRESS.company,
    warehouseCompany:
      shippingConfiguration?.warehouseAddress?.warehouseCompany || GXO_WAREHOUSE_ADDRESS.warehouseCompany,
    line1: shippingConfiguration?.warehouseAddress?.line1 || GXO_WAREHOUSE_ADDRESS.line1,
    city: shippingConfiguration?.warehouseAddress?.city || GXO_WAREHOUSE_ADDRESS.city,
    postalCode: shippingConfiguration?.warehouseAddress?.postalCode || GXO_WAREHOUSE_ADDRESS.postalCode,
    country: shippingConfiguration?.warehouseAddress?.country || GXO_WAREHOUSE_ADDRESS.country,
    countryCode: shippingConfiguration?.warehouseAddress?.countryCode || GXO_WAREHOUSE_ADDRESS.countryCode
  });

  const [deliveryDetails, setDeliveryDetails] = useState({
    name: shippingConfiguration?.deliveryDetails?.name || PH_HEALF_CONTACT_UK.name,
    email: shippingConfiguration?.deliveryDetails?.email || PH_HEALF_CONTACT_UK.email,
    phoneNumber: shippingConfiguration?.deliveryDetails?.phoneNumber || PH_HEALF_CONTACT_UK.phoneNumber
  });

  const [tariffCode, setTariffCode] = useState(shippingConfiguration?.customsDetails?.tariffCode || PH_TARIFF_CODE);
  const [goodsDescription, setGoodsDescription] = useState(
    shippingConfiguration?.customsDetails?.goodsDescription || PH_GOODS_DESCRIPTION
  );
  const [invoiceNumber, setInvoiceNumber] = useState(shippingConfiguration?.customsDetails?.invoiceNumber || "");
  const [shipperVatNumber, setShipperVatNumber] = useState(
    shippingConfiguration?.customsDetails?.shipperVatNumber || vendorShipmentDetails?.ein || ""
  );
  const [shipperEori, setShipperEori] = useState(shippingConfiguration?.customsDetails?.shipperEori || "");
  const [shipperIoss, setShipperIoss] = useState(shippingConfiguration?.customsDetails?.shipperIoss || "");
  const [receiverVatNumber, setReceiverVatNumber] = useState(
    shippingConfiguration?.customsDetails?.receiverVatNumber || PH_HEALF_VAT_UK
  );
  const [receiverEori, setReceiverEori] = useState(
    shippingConfiguration?.customsDetails?.receiverEori || PH_HEALF_EORI_UK
  );
  const [receiverIoss, setReceiverIoss] = useState(shippingConfiguration?.customsDetails?.receiverIoss || "");
  const [collectionDate, setCollectionDate] = useState<string>(
    shippingConfiguration?.collectionDate || getNextBusinessDay()
  );

  // Check if vendor and warehouse are in the same country
  const vendorCurrentAddress = vendorShipmentDetails?.addresses?.find((addr) => addr.isCurrent);
  const isDomesticShipment =
    vendorCurrentAddress?.countryCode?.toUpperCase() === warehouseAddress.countryCode.toUpperCase();

  // Handlers
  const handleWarehouseAddressChange = (field: keyof typeof warehouseAddress, value: string) => {
    setWarehouseAddress((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleDeliveryDetailsChange = (field: keyof typeof deliveryDetails, value: string) => {
    setDeliveryDetails((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const handleTariffCodeChange = (value: string) => {
    setTariffCode(value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.tariffCode;
      return newErrors;
    });
  };

  const fieldToId: Record<string, string> = {
    collectionDate: "collection-date",
    tariffCode: "tariff-code",
    goodsDescription: "goods-description",
    invoiceNumber: "invoice-number",
    shipperVatNumber: "shipper-vat-number",
    shipperEori: "shipper-eori",
    shipperIoss: "shipper-ioss",
    receiverVatNumber: "receiver-vat-number",
    receiverEori: "receiver-eori",
    receiverIoss: "receiver-ioss",
    company: "warehouse-company",
    warehouseCompany: "warehouse-warehouseCompany",
    line1: "warehouse-addressLine",
    city: "warehouse-city",
    postalCode: "warehouse-postalCode",
    country: "warehouse-country",
    countryCode: "warehouse-country-code",
    name: "delivery-name",
    email: "delivery-email",
    phoneNumber: "delivery-phoneNumber"
  };

  // Validation function
  const validateFields = () => {
    const newErrors: Record<string, string> = {};

    // Check collection date
    if (!collectionDate || collectionDate.trim() === "") {
      newErrors.collectionDate = "Collection date is required";
    } else {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(collectionDate)) {
        newErrors.collectionDate = "Collection date must be a valid date";
      } else {
        const date = new Date(collectionDate + "T00:00:00Z");
        if (isNaN(date.getTime())) {
          newErrors.collectionDate = "Collection date must be a valid date";
        } else {
          const now = new Date();
          const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
          if (date <= today) {
            newErrors.collectionDate = "Collection date must be a future date";
          }
          const maxDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 7));
          if (date > maxDate) {
            newErrors.collectionDate = "Collection date must not be more than 7 days in the future";
          }
          const day = date.getUTCDay();
          if (day === 0 || day === 6) {
            newErrors.collectionDate = "Collection date must not fall on a weekend";
          }
        }
      }
    }

    // Check warehouse address fields
    if (!warehouseAddress.company.trim()) newErrors.company = "Company is required";
    if (!warehouseAddress.warehouseCompany.trim()) newErrors.warehouseCompany = "Warehouse Company is required";
    if (!warehouseAddress.line1.trim()) newErrors.line1 = "Address Line is required";
    if (!warehouseAddress.city.trim()) newErrors.city = "City is required";
    if (!warehouseAddress.postalCode.trim()) newErrors.postalCode = "Postal Code is required";
    if (!warehouseAddress.country.trim()) newErrors.country = "Country is required";
    if (!warehouseAddress.countryCode.trim()) newErrors.countryCode = "Country Code is required";

    // Check delivery details fields
    if (!deliveryDetails.name.trim()) newErrors.name = "Name is required";
    if (!deliveryDetails.email.trim()) newErrors.email = "Email is required";
    if (!deliveryDetails.phoneNumber.trim()) newErrors.phoneNumber = "Phone Number is required";

    // Check customs details (only for international shipments)
    if (!isDomesticShipment) {
      if (!tariffCode.trim()) newErrors.tariffCode = "Tariff Code is required";
      if (tariffCode && !/^\d+$/.test(tariffCode)) {
        newErrors.tariffCode = "Tariff Code must be numeric";
      }
      if (!goodsDescription.trim()) newErrors.goodsDescription = "Goods Description is required";
    }

    // Basic email validation
    if (deliveryDetails.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(deliveryDetails.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleConfirm = () => {
    const validationErrors = validateFields();
    if (Object.keys(validationErrors).length > 0) {
      // Scroll to the first error field
      const firstErrorField = Object.keys(validationErrors)[0];
      const id = fieldToId[firstErrorField];
      if (id && scrollContainerRef.current) {
        const errorElement = document.getElementById(id);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    const config: ParcelHeroShippingConfiguration = {
      collectionDate: collectionDate.trim(),
      warehouseAddress,
      deliveryDetails,
      customsDetails: {
        tariffCode,
        goodsDescription: goodsDescription.trim(),
        ...(shipperVatNumber.trim() && { shipperVatNumber: shipperVatNumber.trim() }),
        ...(shipperEori.trim() && { shipperEori: shipperEori.trim() }),
        ...(shipperIoss.trim() && { shipperIoss: shipperIoss.trim() }),
        ...(receiverVatNumber.trim() && { receiverVatNumber: receiverVatNumber.trim() }),
        ...(receiverEori.trim() && { receiverEori: receiverEori.trim() }),
        ...(receiverIoss.trim() && { receiverIoss: receiverIoss.trim() }),
        ...(invoiceNumber.trim() && { invoiceNumber: invoiceNumber.trim() })
      }
    };
    onConfirm(config);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={scrollContainerRef}
        className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="overflow-y-auto max-h-[90vh]">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <LockIcon className="w-6 h-6 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Confirm Parcel Hero Process</h2>
            </div>
            <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <p className="text-sm text-gray-700">
                Please confirm the shipping configuration details before initiating the Parcel Hero process for{" "}
                <span className="font-semibold text-gray-900">{purchaseOrderId}</span>.
              </p>
            </div>

            <div className="space-y-6">
              <Section title="Collection Details">
                <DateInputBox
                  id="collection-date"
                  label="Collection Date"
                  value={collectionDate as ISOString}
                  onChange={(value) => {
                    setCollectionDate(value);
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.collectionDate;
                      return newErrors;
                    });
                  }}
                  required
                  error={errors.collectionDate}
                />
              </Section>

              <Section title="Customs Details">
                {isDomesticShipment ? (
                  <div className="flex items-start space-x-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <svg
                      className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Domestic Shipment</p>
                      <p className="text-xs text-blue-700 mt-1">
                        Customs details are not required for domestic shipments. Both the vendor and warehouse are in
                        the same country ({warehouseAddress.countryCode}).
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <TextInputBox
                          id="tariff-code"
                          label="Tariff Code"
                          value={tariffCode}
                          onChange={handleTariffCodeChange}
                          error={errors.tariffCode}
                          required
                        />
                      </div>
                      <div>
                        <TextInputBox
                          id="goods-description"
                          label="Goods Description"
                          value={goodsDescription}
                          onChange={(value) => {
                            setGoodsDescription(value);
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.goodsDescription;
                              return newErrors;
                            });
                          }}
                          error={errors.goodsDescription}
                          required
                        />
                      </div>
                      <div>
                        <TextInputBox
                          id="invoice-number"
                          label="Invoice Number (Optional)"
                          value={invoiceNumber}
                          onChange={setInvoiceNumber}
                        />
                      </div>
                    </div>

                    <div className="border-t border-neutral-100 pt-4">
                      <h5 className="text-xs font-semibold text-gray-500 mb-3 tracking-wide uppercase">
                        Shipper Tax IDs <span className="text-gray-400 font-normal normal-case">(Optional)</span>
                      </h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <TextInputBox
                            id="shipper-vat-number"
                            label="VAT Number"
                            value={shipperVatNumber}
                            onChange={setShipperVatNumber}
                          />
                        </div>
                        <div>
                          <TextInputBox
                            id="shipper-eori"
                            label="EORI Number"
                            value={shipperEori}
                            onChange={setShipperEori}
                          />
                        </div>
                        <div className="col-span-2">
                          <TextInputBox
                            id="shipper-ioss"
                            label="IOSS Number"
                            value={shipperIoss}
                            onChange={setShipperIoss}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-neutral-100 pt-4">
                      <h5 className="text-xs font-semibold text-gray-500 mb-3 tracking-wide uppercase">
                        Receiver Tax IDs <span className="text-gray-400 font-normal normal-case">(Optional)</span>
                      </h5>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <TextInputBox
                            id="receiver-vat-number"
                            label="VAT Number"
                            value={receiverVatNumber}
                            onChange={setReceiverVatNumber}
                          />
                        </div>
                        <div>
                          <TextInputBox
                            id="receiver-eori"
                            label="EORI Number"
                            value={receiverEori}
                            onChange={setReceiverEori}
                          />
                        </div>
                        <div className="col-span-2">
                          <TextInputBox
                            id="receiver-ioss"
                            label="IOSS Number"
                            value={receiverIoss}
                            onChange={setReceiverIoss}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </Section>

              {vendorName && (
                <Section
                  title="Vendor Details"
                  headerAction={
                    <a
                      href={`${ROUTES_CONFIG.updateVendor.path}?name=${encodeURIComponent(vendorName)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                      title="Update vendor details"
                    >
                      <span>Edit Vendor</span>
                      <ExternalLinkIcon className="w-3 h-3" />
                    </a>
                  }
                >
                  <div className="grid grid-cols-2 gap-4">
                    <ReadOnlyField label="Vendor Name" value={vendorName} />
                    <ReadOnlyField label="Origin" value={vendorOrigin || "—"} />
                    {vendorEmails && vendorEmails.length > 0 && (
                      <div className="col-span-2">
                        <ReadOnlyField label="Emails" value={vendorEmails.join(", ")} />
                      </div>
                    )}
                    {vendorShipmentDetails?.contactPerson && (
                      <ReadOnlyField label="Contact Person" value={vendorShipmentDetails.contactPerson} />
                    )}
                    {vendorShipmentDetails?.contactInfo && (
                      <ReadOnlyField label="Contact Info" value={vendorShipmentDetails.contactInfo} />
                    )}
                    {vendorShipmentDetails?.email && (
                      <ReadOnlyField label="Shipment Email" value={vendorShipmentDetails.email} />
                    )}
                    {vendorShipmentDetails?.opsPOC && (
                      <ReadOnlyField label="Ops POC" value={vendorShipmentDetails.opsPOC} />
                    )}
                  </div>

                  {vendorShipmentDetails?.addresses && vendorShipmentDetails.addresses.length > 0 && (
                    <div className="border-t border-neutral-100 pt-4">
                      <h5 className="text-xs font-semibold text-gray-500 mb-3 tracking-wide uppercase">
                        Vendor Address
                      </h5>
                      {vendorShipmentDetails.addresses
                        .filter((addr) => addr.isCurrent)
                        .map((addr, idx) => (
                          <div key={idx} className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <ReadOnlyField
                                label="Address"
                                value={[addr.line1, addr.line2, addr.line3].filter(Boolean).join(", ")}
                              />
                            </div>
                            <ReadOnlyField label="City" value={addr.city} />
                            <ReadOnlyField label="Zip Code" value={addr.zipCode} />
                            <ReadOnlyField label="Country" value={addr.country || "—"} />
                            <ReadOnlyField label="Country Code" value={addr.countryCode} />
                          </div>
                        ))}
                    </div>
                  )}
                </Section>
              )}

              <Section title="Warehouse Address">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <TextInputBox
                      id="warehouse-company"
                      label="Company"
                      value={warehouseAddress.company}
                      onChange={(value) => handleWarehouseAddressChange("company", value)}
                      error={errors.company}
                      required
                    />
                  </div>
                  <div>
                    <TextInputBox
                      id="warehouse-warehouseCompany"
                      label="Warehouse Company"
                      value={warehouseAddress.warehouseCompany}
                      onChange={(value) => handleWarehouseAddressChange("warehouseCompany", value)}
                      error={errors.warehouseCompany}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <TextInputBox
                      id="warehouse-addressLine"
                      label="Address Line"
                      value={warehouseAddress.line1}
                      onChange={(value) => handleWarehouseAddressChange("line1", value)}
                      error={errors.line1}
                      required
                    />
                  </div>
                  <div>
                    <TextInputBox
                      id="warehouse-city"
                      label="City"
                      value={warehouseAddress.city}
                      onChange={(value) => handleWarehouseAddressChange("city", value)}
                      error={errors.city}
                      required
                    />
                  </div>
                  <div>
                    <TextInputBox
                      id="warehouse-postalCode"
                      label="Postal Code"
                      value={warehouseAddress.postalCode}
                      onChange={(value) => handleWarehouseAddressChange("postalCode", value)}
                      error={errors.postalCode}
                      required
                    />
                  </div>
                  <div>
                    <TextInputBox
                      id="warehouse-country"
                      label="Country"
                      value={warehouseAddress.country}
                      onChange={(value) => handleWarehouseAddressChange("country", value)}
                      error={errors.country}
                      required
                    />
                  </div>
                  <div>
                    <TextInputBox
                      id="warehouse-country-code"
                      label="Country Code"
                      value={warehouseAddress.countryCode}
                      onChange={(value) => handleWarehouseAddressChange("countryCode", value)}
                      error={errors.countryCode}
                      required
                    />
                  </div>
                </div>
              </Section>

              <Section title="Delivery Details">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <TextInputBox
                      id="delivery-name"
                      label="Name"
                      value={deliveryDetails.name}
                      onChange={(value) => handleDeliveryDetailsChange("name", value)}
                      error={errors.name}
                      required
                    />
                  </div>
                  <div>
                    <TextInputBox
                      id="delivery-email"
                      label="Email"
                      type="email"
                      value={deliveryDetails.email}
                      onChange={(value) => handleDeliveryDetailsChange("email", value)}
                      error={errors.email}
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <TextInputBox
                      id="delivery-phoneNumber"
                      label="Phone Number"
                      type="tel"
                      value={deliveryDetails.phoneNumber}
                      onChange={(value) => handleDeliveryDetailsChange("phoneNumber", value)}
                      error={errors.phoneNumber}
                      required
                    />
                  </div>
                </div>
              </Section>
            </div>

            {effectiveCost !== undefined && (
              <div className="flex items-center justify-between border-t border-gray-200 pt-4 mt-6">
                <span className="text-sm text-gray-500">{costLabel}</span>
                <span className="text-sm font-semibold text-gray-900">£{effectiveCost.toFixed(2)}</span>
              </div>
            )}

            <p className="text-xs text-gray-500 border-t border-gray-200 pt-4 mt-4">
              Once confirmed, the Parcel Hero shipping process will be initiated. Please ensure all details are correct.
            </p>
          </div>

          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex-1 mr-4">
              {Object.keys(errors).length > 0 && (
                <div className="flex items-start space-x-2 text-sm text-red-600">
                  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <div className="space-y-0.5">
                    {Object.values(errors).map((error, index) => (
                      <p key={index}>{error}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex space-x-3 flex-shrink-0">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-primary-800 text-white rounded-md hover:bg-primary-900 transition-colors"
                onClick={handleConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartParcelHeroConfirmation;

const Section = ({
  title,
  headerAction,
  children
}: {
  title: string;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
    <div className="flex items-center justify-between mb-4">
      <h4 className="text-sm font-semibold text-black tracking-wide uppercase">{title}</h4>
      {headerAction}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <p className="text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">{value}</p>
  </div>
);
